import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import IcomoonReact from 'icomoon-react';
import iconSet from '../images/teambuildr-selection.json';
import OUTDATED_BUILD_MESSAGE from './outdatedBuildMessage';

/* Colours read CSS variables so a module that renders dark (Build, via its
 * BuildThemeProvider) can recolour toasts; the fallbacks are the app's light values. */

const ToastContent = ({ message, onUndo, closeToast }) => (
  <div style={{
    width: '100%',
    fontFamily: 'Nunito Sans',
  }}
  >
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      background: 'var(--tb-toast-surface, #ffffff)',
      padding: '12px 16px',
      borderRadius: '8px',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
    }}
    >
      <span style={{
        fontFamily: 'Nunito Sans',
        fontWeight: 600,
        color: 'var(--tb-toast-text, #333)',
      }}
      >
        {message}
      </span>
      {onUndo && (
        <button
          type='button'
          onClick={(e) => {
            e.stopPropagation();
            onUndo();
            if (closeToast) {
              closeToast();
            }
          }}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--tb-toast-accent, #4A90E2)',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            marginLeft: '16px',
            padding: 0,
            fontFamily: 'Nunito Sans',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <IcomoonReact
            iconSet={iconSet}
            size={14}
            icon='undo'
            color='var(--tb-toast-accent, #4A90E2)'
          />
          UNDO
        </button>
      )}
    </div>
  </div>
);

const OptionsToastContent = ({ message, onOptionSelect, closeToast }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [selectedOption, setSelectedOption] = useState('single');

  const handleToggle = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    if (onOptionSelect) {
      onOptionSelect(option);
    }
    // Close toast after selection
    setTimeout(() => {
      if (closeToast) {
        closeToast();
      }
    }, 300);
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      width: '100%',
      fontFamily: 'Nunito Sans',
    }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        background: 'var(--tb-toast-surface, #ffffff)',
        padding: '12px 16px',
        borderRadius: '8px',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
      }}
      >
        <span style={{
          fontFamily: 'Nunito Sans',
          fontWeight: 600,
          color: 'var(--tb-toast-text, #333)',
        }}
        >
          {message}
        </span>
        <button
          type='button'
          onClick={handleToggle}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--tb-toast-accent, #4A90E2)',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            marginLeft: '16px',
            padding: 0,
            fontFamily: 'Nunito Sans',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          Options
          <IcomoonReact
            iconSet={iconSet}
            size={12}
            icon='down-arrow'
            color='var(--tb-toast-muted, #E0E0E0)'
            style={{
              display: 'inline-block',
              transition: 'transform 0.2s ease',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </button>
      </div>
      {isOpen && (
        <div style={{
          animation: 'slideDown 0.2s ease forwards',
          overflow: 'hidden',
          background: 'var(--tb-toast-surface, #ffffff)',
          borderRadius: '8px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
        }}
        >
          <button
            type='button'
            onClick={() => handleOptionClick('single')}
            style={{
              width: '100%',
              padding: '16px',
              background: 'transparent',
              border: 'none',
              textAlign: 'left',
              fontFamily: 'Nunito Sans',
              fontSize: '14px',
              color: selectedOption === 'single' ? 'var(--tb-toast-text, #333)' : 'var(--tb-toast-muted, #999)',
              fontWeight: selectedOption === 'single' ? 600 : 400,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'background-color 0.15s ease',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--tb-toast-hover, #f9f9f9)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            Update only this instance
            {selectedOption === 'single' && (
              <IcomoonReact iconSet={iconSet} size={12} icon='checkmark' color='var(--tb-toast-muted, #9E9E9E)' />
            )}
          </button>
          <button
            type='button'
            onClick={() => handleOptionClick('all')}
            style={{
              width: '100%',
              padding: '16px',
              background: 'transparent',
              border: 'none',
              textAlign: 'left',
              fontFamily: 'Nunito Sans',
              fontSize: '14px',
              color: selectedOption === 'all' ? 'var(--tb-toast-text, #333)' : 'var(--tb-toast-muted, #999)',
              fontWeight: selectedOption === 'all' ? 600 : 400,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'background-color 0.15s ease',
              borderBottomLeftRadius: '8px',
              borderBottomRightRadius: '8px',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--tb-toast-hover, #f9f9f9)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            Update all future instances
            {selectedOption === 'all' && (
              <IcomoonReact iconSet={iconSet} size={12} icon='checkmark' color='var(--tb-toast-muted, #9E9E9E)' />
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export const showErrorToast = (message, toastOptions = {}) => (
  /** TEMPORARY: the outdated-build refresh toast already shows this (outdatedBuildInterceptor). */
  message === OUTDATED_BUILD_MESSAGE ? null : toast.error(
    ({ closeToast }) => (
      <ToastContent message={message} closeToast={closeToast} />
    ),
    {
      position: 'top-right',
      autoClose: 5000,
      closeOnClick: true,
      pauseOnHover: true,
      ...toastOptions,
    },
  )
);

export const showSuccessToast = (message, onUndo = null, toastOptions = {}) => (
  toast.success(
    ({ closeToast }) => (
      <ToastContent message={message} onUndo={onUndo} closeToast={closeToast} />
    ),
    {
      position: 'top-right',
      autoClose: 5000,
      closeOnClick: true,
      pauseOnHover: true,
      ...toastOptions,
    },
  )
);

export const showInfoToast = (message, toastOptions = {}) => (
  toast.info(
    ({ closeToast }) => (
      <ToastContent message={message} closeToast={closeToast} />
    ),
    {
      position: 'top-right',
      autoClose: 5000,
      closeOnClick: true,
      pauseOnHover: true,
      ...toastOptions,
    },
  )
);

export const showWarningToast = (message, toastOptions = {}) => (
  toast.warning(
    ({ closeToast }) => (
      <ToastContent message={message} closeToast={closeToast} />
    ),
    {
      position: 'top-right',
      autoClose: 5000,
      closeOnClick: true,
      pauseOnHover: true,
      ...toastOptions,
    },
  )
);

const RefreshToastContent = ({ message, onRefresh }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    background: 'var(--tb-toast-surface, #ffffff)',
    padding: '12px 16px',
    borderRadius: '8px',
    /** Static, not a countdown — this toast stays until the user refreshes. Matches the
     * error toasts' progress bar. */
    borderBottom: '4px solid red',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
    fontFamily: 'Nunito Sans',
  }}
  >
    <span style={{ fontWeight: 600, color: 'var(--tb-toast-text, #333)' }}>
      {message}
    </span>
    <button
      type='button'
      onClick={onRefresh}
      style={{
        background: 'none',
        border: 'none',
        color: 'var(--tb-toast-accent, #4A90E2)',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        marginLeft: '16px',
        padding: 0,
        fontFamily: 'Nunito Sans',
      }}
    >
      REFRESH
    </button>
  </div>
);

RefreshToastContent.propTypes = {
  message: PropTypes.string.isRequired,
  onRefresh: PropTypes.func.isRequired,
};

const REFRESH_TOAST_ID = 'version-update';

/** Shares the service worker's 'version-update' id so the two refresh prompts never stack.
 * If that prompt is already up, take it over so this message isn't silently dropped. */
export const showRefreshToast = (message, onRefresh) => {
  const render = () => <RefreshToastContent message={message} onRefresh={onRefresh} />;

  if (toast.isActive(REFRESH_TOAST_ID)) {
    toast.update(REFRESH_TOAST_ID, { render });
    return;
  }

  toast.info(render, {
    position: 'top-right',
    autoClose: false,
    closeOnClick: false,
    hideProgressBar: true,
    toastId: REFRESH_TOAST_ID,
  });
};

export const showOptionsToast = (message, onOptionSelect) => {
  toast.success(
    ({ closeToast }) => (
      <OptionsToastContent
        message={message}
        onOptionSelect={onOptionSelect}
        closeToast={closeToast}
      />
    ),
    {
      position: 'top-right',
      autoClose: 10000, // Don't auto-close so user can interact
      closeOnClick: false, // Prevent closing on click to allow interaction
      pauseOnHover: true,
      toastId: 'options-toast', // Prevent duplicates
      className: 'options-toast', // Custom class for styling
    },
  );
};
